import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ArrowUpRight, Sparkles, Check } from "lucide-react";

const TOOL_CARDS = [
    {
        title: "AI Article Writer",
        path: "/category/article-content",
        bg: "bg-[#A7F3D0]",
        desc: "Helps users create high-quality blog posts and SEO articles quickly.",
        features: [
            "Generate articles in seconds",
            "SEO-friendly structure",
            "Beginner-friendly interface",
            "Fast content creation",
        ],
        useCase:
            "This tool is useful for bloggers who want to publish content regularly without spending hours writing manually.",
        cta: "Try the AI Article Writer",
    },
    {
        title: "AI Script Generator (Free Online)",
        path: "/tool/ai-script-generator",
        bg: "bg-[#D8B4FE]",
        desc: "Helps creators write engaging scripts for YouTube videos, Instagram Reels, podcasts, and ads.",
        listLabel: "Best For",
        features: [
            "YouTubers",
            "Video creators",
            "Influencers",
            "Digital marketers",
        ],
        useCase:
            "Creating scripts manually can be difficult, especially when you need fresh ideas daily. AI tools help generate scripts quickly and improve creativity.",
        cta: "Use the AI Script Generator",
    },
    {
        title: "Free Product Description Generator",
        path: "/tool/product-description",
        bg: "bg-[#FDBA74]",
        desc: "Writing product descriptions for eCommerce stores takes time. This AI tool helps online sellers generate professional product descriptions instantly.",
        listLabel: "Perfect For",
        features: [
            "Shopify stores",
            "Amazon sellers",
            "Dropshipping businesses",
            "Small online stores",
        ],
        useCase:
            "Good product descriptions can improve conversions and make products more attractive to buyers.",
        cta: "Try the Product Description Generator",
    },
    {
        title: "Hook Sentence Generator for Blogs & Videos",
        path: "/tool/hook-sentence",
        bg: "bg-[#93C5FD]",
        desc: "Helps create attention-grabbing opening lines for blogs, YouTube videos, and social media posts.",
        listLabel: "Strong hooks help improve",
        features: [
            "User engagement",
            "Click-through rate",
            "Audience retention",
        ],
        useCase:
            "Many successful creators use strong hooks to keep users interested in their content.",
        cta: "Use the Hook Sentence Generator",
    },
];

const FAQS = [
    {
        q: "Are AI writing tools free to use?",
        a: "Yes, many AI tools on Free AI Writers are completely free.",
    },
    {
        q: "Which AI tool is best for bloggers?",
        a: "AI Article Writers and AI Script Generators are very useful for bloggers and content creators.",
    },
    {
        q: "Can AI-generated content rank on Google?",
        a: "Yes, high-quality AI-assisted content with proper SEO can rank on Google.",
    },
    {
        q: "Are AI tools useful for students?",
        a: "Yes, students can use AI tools for essays, assignments, summaries, and research ideas.",
    },
];

const EXTRA_TOOLS = [
    { name: "AI Paragraph Generator", slug: "paragraph-generator" },
    { name: "AI Email Writer", slug: "email-writer" },
    { name: "AI Story Generator", slug: "story-generator" },
    { name: "AI Caption Generator", slug: "tiktok-comment-generator" },
    { name: "AI Code Generator", slug: "ai-code-writer" },
];

export default function HomeArticle() {
    const [openFaq, setOpenFaq] = useState(0);

    // JSON-LD FAQ schema for Google rich snippets
    const faqSchema = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: FAQS.map((q) => ({
            "@type": "Question",
            name: q.q,
            acceptedAnswer: { "@type": "Answer", text: q.a },
        })),
    };

    return (
        <article
            id="article"
            data-testid="home-article"
            className="mt-24 md:mt-32"
        >
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
            />

            {/* HEADLINE BLOCK */}
            <header className="nb-border-4 nb-shadow-lg rounded-3xl bg-[#FDE047] p-6 md:p-12">
                <div className="text-xs font-bold uppercase tracking-widest mb-3">
                    03 — long read
                </div>
                <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter leading-[1.05]">
                    Best Free AI Writing Tools for Bloggers and Students in 2026
                </h2>
                <p className="mt-4 text-base md:text-xl font-bold text-black/80 max-w-3xl">
                    Free AI Tools to Write Articles, Scripts & SEO Content Faster
                </p>
            </header>

            {/* INTRO */}
            <section className="mt-8 nb-border-4 rounded-3xl bg-white p-6 md:p-10 nb-shadow space-y-4 text-base md:text-lg leading-relaxed">
                <p>
                    Creating high-quality content every day is difficult for
                    bloggers, students, freelancers, and digital marketers.
                    Writing articles, YouTube scripts, product descriptions, and
                    social media captions takes time and effort. That's why many
                    creators are now using AI writing tools to save time and
                    improve productivity.
                </p>
                <p>
                    If you are searching for the <b>best free AI writing tools
                    for bloggers and students</b>, <b>Free AI Writers</b> offers
                    powerful and beginner-friendly AI tools that help users
                    create content quickly and easily.
                </p>
                <p>
                    Whether you want to write SEO articles, generate scripts, or
                    create product descriptions, AI tools can make content
                    creation faster and smarter.
                </p>
            </section>

            {/* WHY POPULAR + BENEFITS */}
            <section className="mt-8 grid md:grid-cols-2 gap-6">
                <div className="nb-border-4 rounded-3xl bg-[#A7F3D0] p-6 md:p-8 nb-shadow">
                    <h3 className="font-display text-2xl md:text-3xl font-black tracking-tight">
                        Why Free AI Writing Tools Are Popular
                    </h3>
                    <p className="mt-3 text-base leading-relaxed">
                        AI content tools are becoming popular because they help
                        users generate content within seconds. Instead of
                        spending hours writing manually, creators can use
                        artificial intelligence to generate ideas, outlines, and
                        complete drafts instantly.
                    </p>
                </div>
                <div className="nb-border-4 rounded-3xl bg-white p-6 md:p-8 nb-shadow">
                    <h3 className="font-display text-2xl md:text-3xl font-black tracking-tight">
                        Benefits of AI Writing Tools
                    </h3>
                    <ul className="mt-3 space-y-2">
                        {[
                            "Save time while writing articles",
                            "Create SEO-friendly blog posts",
                            "Generate YouTube video scripts",
                            "Improve productivity for bloggers",
                            "Write product descriptions quickly",
                            "Help students with assignments and ideas",
                            "Easy to use for beginners",
                        ].map((b) => (
                            <li
                                key={b}
                                className="flex items-start gap-2 text-base"
                            >
                                <Check
                                    className="w-5 h-5 mt-0.5 shrink-0"
                                    strokeWidth={3}
                                />
                                <span>{b}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            <p className="mt-6 text-base md:text-lg leading-relaxed nb-border rounded-2xl bg-white p-5 nb-shadow-sm">
                Many successful bloggers and content creators now use AI tools
                daily to improve workflow and publish content consistently.
            </p>

            {/* TOOLS SECTION HEADER */}
            <section className="mt-12">
                <h3 className="font-display text-3xl md:text-4xl font-black tracking-tighter">
                    Best AI Writing Tools Available on Free AI Writers
                </h3>
                <p className="mt-3 text-base md:text-lg text-neutral-800">
                    Free AI Writers provides multiple AI-powered tools designed
                    for different types of content creation.
                </p>
            </section>

            {/* TOOL CARDS */}
            <section className="mt-6 grid md:grid-cols-2 gap-6">
                {TOOL_CARDS.map((t) => (
                    <article
                        key={t.title}
                        className={`${t.bg} nb-border-4 rounded-3xl p-6 md:p-8 nb-shadow flex flex-col`}
                    >
                        <h4 className="font-display text-2xl md:text-3xl font-black tracking-tight">
                            {t.title}
                        </h4>
                        <p className="mt-3 text-base leading-relaxed">
                            {t.desc}
                        </p>
                        <div className="mt-5 nb-border rounded-2xl bg-white p-4">
                            <div className="text-xs font-bold uppercase tracking-widest text-neutral-700">
                                {t.listLabel || "Features"}
                            </div>
                            <ul className="mt-2 space-y-1.5">
                                {t.features.map((f) => (
                                    <li
                                        key={f}
                                        className="flex items-start gap-2 text-sm font-medium"
                                    >
                                        <span className="mt-2 w-1.5 h-1.5 bg-black rounded-full shrink-0" />
                                        <span>{f}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <p className="mt-4 text-sm leading-relaxed text-black/85 flex-1">
                            {t.useCase}
                        </p>
                        <Link
                            to={t.path}
                            data-testid={`home-article-cta-${t.path.split("/").pop()}`}
                            className="mt-5 self-start inline-flex items-center gap-2 bg-black text-white nb-border rounded-xl px-5 py-3 font-bold uppercase tracking-widest text-xs nb-shadow-sm hover:-translate-y-1 hover:shadow-[6px_6px_0px_#0A0A0A] active:translate-y-1 active:translate-x-1 active:shadow-none transition-all"
                        >
                            {t.cta}
                            <ArrowUpRight className="w-4 h-4" strokeWidth={3} />
                        </Link>
                    </article>
                ))}
            </section>

            {/* WHY CHOOSE */}
            <section className="mt-12 nb-border-4 rounded-3xl bg-[#0A0A0A] text-[#FAFAFA] p-6 md:p-12 nb-shadow-lg">
                <div className="text-xs font-bold uppercase tracking-widest text-[#FDE047]">
                    why us
                </div>
                <h3 className="font-display text-3xl md:text-4xl lg:text-5xl font-black tracking-tighter mt-2">
                    Why Choose FreeAIWriters.com?
                </h3>
                <p className="mt-4 text-base md:text-lg text-neutral-300 leading-relaxed max-w-3xl">
                    FreeAIWriters.com is designed for bloggers, students,
                    freelancers, marketers, and content creators who want fast
                    and easy AI-powered writing tools. Unlike many complicated
                    AI platforms, FreeAIWriters.com focuses on simplicity,
                    speed, and user-friendly tools that help users create
                    content without technical knowledge.
                </p>
                <div className="mt-6 grid sm:grid-cols-2 gap-3">
                    {[
                        "Free and easy-to-use AI tools",
                        "Beginner-friendly interface",
                        "Fast content generation",
                        "Multiple AI writing tools in one place",
                        "SEO-friendly content creation",
                        "Mobile-friendly website",
                        "No complicated setup required",
                        "Helpful tools for blogging, marketing, and content writing",
                    ].map((b) => (
                        <div
                            key={b}
                            className="flex items-start gap-2 nb-border rounded-xl bg-[#1a1a1a] px-4 py-3 text-sm font-medium"
                        >
                            <Sparkles
                                className="w-4 h-4 mt-0.5 shrink-0 text-[#FDE047]"
                                strokeWidth={3}
                            />
                            <span>{b}</span>
                        </div>
                    ))}
                </div>
                <p className="mt-6 text-base text-neutral-300 leading-relaxed max-w-3xl">
                    Whether you need to generate blog posts, scripts, product
                    descriptions, captions, or marketing content,
                    FreeAIWriters.com provides simple AI solutions to improve
                    productivity and save time.
                </p>
            </section>

            {/* FAQ */}
            <section className="mt-12 nb-border-4 rounded-3xl bg-white p-6 md:p-10 nb-shadow">
                <h3 className="font-display text-3xl md:text-4xl font-black tracking-tighter">
                    Frequently Asked Questions
                </h3>
                <div className="mt-5 space-y-3">
                    {FAQS.map((q, i) => {
                        const open = openFaq === i;
                        return (
                            <div
                                key={i}
                                data-testid={`home-faq-${i}`}
                                className="nb-border rounded-2xl bg-neutral-50 overflow-hidden"
                            >
                                <button
                                    type="button"
                                    onClick={() => setOpenFaq(open ? -1 : i)}
                                    className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left font-bold hover:bg-[#FDE047] transition-colors"
                                >
                                    <span>{q.q}</span>
                                    <ChevronDown
                                        className={`w-5 h-5 shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
                                        strokeWidth={3}
                                    />
                                </button>
                                {open && (
                                    <div className="px-5 pb-4 pt-1 text-base text-neutral-800 leading-relaxed border-t-2 border-black/10">
                                        {q.a}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </section>

            {/* FINAL THOUGHTS */}
            <section className="mt-12 nb-border-4 rounded-3xl bg-[#A7F3D0] p-6 md:p-12 nb-shadow">
                <h3 className="font-display text-3xl md:text-4xl lg:text-5xl font-black tracking-tighter">
                    Final Thoughts
                </h3>
                <p className="mt-4 text-base md:text-lg leading-relaxed">
                    AI writing technology is helping bloggers, students,
                    businesses, and creators produce content faster than ever
                    before. Whether you want to write articles, create scripts,
                    generate product descriptions, or improve SEO, AI tools can
                    save time and increase productivity.
                </p>

                <p className="mt-6 font-bold">
                    You can also explore more tools on Free AI Writers like:
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                    {EXTRA_TOOLS.map((t) => (
                        <Link
                            key={t.slug}
                            to={`/tool/${t.slug}`}
                            data-testid={`home-article-extra-${t.slug}`}
                            className="nb-border bg-white text-black text-sm font-bold px-4 py-2 rounded-full hover:bg-[#FDE047] hover:-translate-y-[2px] hover:shadow-[2px_2px_0px_#0A0A0A] active:translate-y-[2px] active:translate-x-[2px] active:shadow-none transition-all"
                        >
                            {t.name}
                        </Link>
                    ))}
                </div>

                <Link
                    to="/"
                    data-testid="home-article-visit-cta"
                    className="mt-7 inline-flex items-center gap-2 bg-black text-white nb-border-4 rounded-xl px-6 py-4 font-black uppercase tracking-widest text-sm nb-shadow hover:-translate-y-1 hover:shadow-[6px_6px_0px_#0A0A0A] active:translate-y-1 active:translate-x-1 active:shadow-none transition-all"
                >
                    Visit Free AI Writers
                    <ArrowUpRight className="w-4 h-4" strokeWidth={3} />
                </Link>
            </section>
        </article>
    );
}
